import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { throttle } from 'lodash';
import {
  ExCard,
  ExEmptyState,
  ExPagination,
  ExButton,
  ButtonType,
  ButtonSize,
  ButtonFlavor,
  ExTooltip,
  TooltipPosition,
  TooltipAlignment,
  ExLoader,
  LoaderSize,
  LoaderVariant,
  ExIcon,
} from '@boomi/exosphere';
import noResultsImage from '@assets/boomi-ai/ui-kit/agentFoundation/No_result.svg';
import { ModelsTableProps } from '../types/models.types';
import ModelRowActions from '../ModelRowActions/ModelRowActions';
import { providerLogo } from '@components/ModelsManagement';
import './ModelsCardView.scss';
import { formatDate } from '@components/ListViewUtils/ListViewUtils';

const ITEMS_PER_PAGE_OPTIONS = [15, 30, 45];

function ModelsCardView({
  models = [],
  isLoading = false,
  onModelSelect,
  onEdit,
  onDelete,
  onViewModels,
  searchTerm = '',
  isAnyFilterActive = false,
  showEmptyState = true,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setShowEmptyState,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  hasEditPermission = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  hasCreatePermission = false,
  currentPage = 1,
  pageSize = 10,
  totalItems = 0,
  onPageChange,
  onPageSizeChange,
  isInitialLoading = isLoading,
  selectedModelId = '',
}: Readonly<ModelsTableProps & {
  currentPage?: number;
  pageSize?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  isAnyFilterActive?: boolean;
  isInitialLoading?: boolean;
  selectedModelId?: string;
}>) {
  const [t] = useTranslation('common');
  const openDropdownRef = useRef<any>(null);

  const closeOpenDropdown = () => {
    if (openDropdownRef.current && typeof openDropdownRef.current.closeDropdown === 'function') {
      openDropdownRef.current.closeDropdown();
      openDropdownRef.current = null;
    }
  };

  const throttledCloseDropdown = useRef(throttle(closeOpenDropdown, 300)).current;

  const renderPagination = () => {
    if (totalItems === 0 && models.length === 0) return null;

    return (
      <div className="models-card-view__pagination">
        <ExPagination
          data-testid="models-card-pagination"
          totalItems={totalItems || models.length}
          pageSize={pageSize}
          pageSizeOptions={ITEMS_PER_PAGE_OPTIONS}
          selectedPage={currentPage}
          onChange={(e: CustomEvent) => {
            if (e.detail.pageSize !== pageSize) {
              onPageSizeChange?.(e.detail.pageSize);
            } else {
              onPageChange?.(e.detail.selectedPage || 1);
            }
          }}
          className="models-card-view-pagination"
        />
      </div>
    );
  };

  if (isLoading && isInitialLoading) {
    return (
      <div className="models-card-parent-container">
        <div className="models-card-view models-card-view--loading">
          <ExLoader
            size={LoaderSize.MEDIUM}
            variant={LoaderVariant.SPINNER}
            label={t('models_loading')}
            data-testid="models-card-loader"
          />
        </div>
      </div>
    );
  }

  if (isLoading && !isInitialLoading) {
    return (
      <div className="models-card-parent-container">
        <div className="models-card-view models-card-view--loading">
          <ExLoader
            size={LoaderSize.MEDIUM}
            variant={LoaderVariant.SPINNER}
            label={t('models_loading')}
            data-testid="models-card-loader"
          />
        </div>
        {renderPagination()}
      </div>
    );
  }

  if (showEmptyState && models.length === 0) {
    return (
      <div className="models-card-parent-container">
        <div className="models-card-view models-card-view--empty">
          <ExEmptyState
            label={searchTerm !== '' || isAnyFilterActive ? t('model_configs_search_result_empty_state_title') : t('model_configs_empty_state_title')}
            text={searchTerm !== '' || isAnyFilterActive ? t('model_configs_search_result_empty_state_text') : t('model_configs_empty_state_text')}
            data-testid="models-card-empty-state"
          >
            <div slot="header" data-testid="models-card-empty-state-header">
              <img alt={t('models_card_no_models_alt')} src={noResultsImage} data-testid="models-card-empty-state-image" />
            </div>
          </ExEmptyState>
        </div>
      </div>
    );
  }
  return (
    <div className="models-card-parent-container">
      <div
        className="models-card-container"
        onScroll={() => {
          if (openDropdownRef.current) {
            throttledCloseDropdown();
          }
        }}
      >
        {models.map((model) => (
          <ExCard
            key={model.id}
            className={`models-card ${model.id === selectedModelId ? 'models-card-selected' : ''}`}
          >
            {/* Provider Icon */}
            <div className="models-card__icon-section">
              <div className="models-card__icon-wrapper" data-testid="models-card-model-icon">
                <img
                  src={providerLogo(model.provider).src}
                  alt={providerLogo(model.provider).alt}
                  className="models-card__icon"
                />
              </div>
            </div>

            {/* Header */}
            <div className="models-card__header">
              <div className="models-card__title-section">
                <button
                  type="button"
                  className="models-card__model-name"
                  onClick={() => onModelSelect?.(model)}
                  data-testid="models-card-model-name"
                >
                  {model.name}
                </button>
                <span className="models-card__model-id" data-testid="models-card-model-id">
                  {t('models_card_id_label')}
                  {' '}
                  {model.id}
                </span>
              </div>
            </div>

            {/* Content - Label/Value pairs */}
            <div className="models-card__body">
              {/* Two-column layout for Environment and Deployed Models */}
              <div className="models-card__two-column">
                <div className="models-card__column">
                  <span className="models-card__label">{t('models_card_environment')}</span>
                  {model.environments && model.environments.length > 0 ? (
                    (() => {
                      const envNames = model.environments.map(
                        (env: any) => env.environment_name || env,
                      );
                      const firstEnv = envNames[0];
                      const remaining = envNames.length - 1;
                      const displayText = remaining > 0
                        ? `${firstEnv} (+${remaining})`
                        : firstEnv;

                      return (
                        <ExTooltip
                          tooltipDelay={0}
                          position={TooltipPosition.TOP}
                          alignment={TooltipAlignment.MIDDLE}
                        >
                          <div style={{
                            overflowWrap: 'anywhere',
                            width: '100%',
                            overflow: 'hidden',
                            whiteSpace: 'normal',
                          }}
                          >
                            {envNames.length > 0 ? envNames.join(', ') : '-'}
                          </div>
                          <div
                            slot="anchor"
                            style={{
                              color: 'var(--exo-color-font)',
                              cursor: 'help',
                              fontSize: 'var(--exo-font-size-small)',
                            }}
                          >
                            {displayText}
                          </div>
                        </ExTooltip>
                      );
                    })()
                  ) : (
                    <span className="models-card__value" data-testid="models-card-environments">{model.environments || '-'}</span>
                  )}
                </div>
                <div className="models-card__column">
                  <span className="models-card__label">{t('models_table_column_model')}</span>
                  {model?.model_count && model.model_count > 0 ? (
                    <button
                      type="button"
                      className="models-card__deployed-models-link"
                      onClick={() => onViewModels?.(
                        model?.id || '',
                        model.name || '',
                        model.provider,
                      )}
                      data-testid="models-card-deployed-models-link"
                    >
                      {model.model_count}
                    </button>
                  ) : (
                    <span className="models-card__value" data-testid="models-card-deployed-models-count">{model.model_count || 0}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="models-card__divider" />

            {/* Footer */}
            <div className="models-card__footer">
              <div className="models-card__footer-actions">
                <ExButton
                  type={ButtonType.SECONDARY}
                  size={ButtonSize.DEFAULT}
                  flavor={ButtonFlavor.BRANDED}
                  onClick={() => onEdit?.(model)}
                  data-testid="models-card-footer-edit"
                >
                  <ExIcon
                    icon="Edit"
                    slot="prefix"
                    hideBrowserTooltip
                  />
                  {t('edit', 'Edit')}
                </ExButton>
                <ModelRowActions
                  model={model}
                  onView={() => { throttledCloseDropdown(); onModelSelect?.(model); }}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  showEdit={false}
                  t={t}
                  openDropdownRef={openDropdownRef}
                />
              </div>
              <span className="models-card__last-updated">
                {t('models_card_updated')}
                {': '}
                {formatDate(model.last_updated_on || '')}
              </span>
            </div>
          </ExCard>
        ))}
      </div>
      {models.length > 0 && renderPagination()}
    </div>
  );
}

export default ModelsCardView;
